import { useAutoAnimate } from '@formkit/auto-animate/react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import router from 'next/router';
import { useMemo } from 'react';
import CircleLoader from 'react-spinners/ClipLoader';

import { FlowListItem, FlowTemplates } from '@/components/flows';
import { api } from '@/levity/api';
import { useAuthSession } from '@/levity/auth/compatibility-components';
import { copy } from '@/levity/flows/copy';
import { urls } from '@/levity/flows/core';
import { useCloneTemplate, useDuplicateFlow } from '@/levity/flows/hooks';
import { templates } from '@/levity/flows/templates';
import type { Flow } from '@/levity/flows/types';
import type { IntegrationAuthApp } from '@/levity/integrations/types';
import { type LayoutPage, LayoutTypes } from '@/pages/_layouts';
import { Button, ButtonVariant, Collection, Dialog, Layout, ToastType, useConfirmDialog, useToast } from '@/ui-library';
import withSSRSession from '@/utils/withSSRSession';

import { PageTitle } from '../../components/shared/page-title.component';

const FlowOverviewPage: LayoutPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ template }) => {
  const queryClient = useQueryClient();
  const utils = api.useUtils();
  const session = useAuthSession();

  const [aimationParent] = useAutoAnimate();
  const { showDialog } = useConfirmDialog();
  const { clone: cloneTemplate, isCloning: isCloningTemplate } = useCloneTemplate();

  const { isLoading, data: flows = [] } = api.flows.getAll.useQuery(undefined, {
    onSettled: () => {
      if (template) {
        cloneTemplate(template.integration, template.templateName, 'Website');
      }
    },
  });

  const { showToast } = useToast();

  const flowsQueryKeyId = useMemo(() => getQueryKey(api.flows.getAll, undefined, 'query'), []);

  const showInternalTemplates = session?.user?.email?.endsWith('@levity.ai') ?? false;

  const updateMutation = api.flows.updateOne.useMutation({
    onMutate: async (newFlow) => {
      await queryClient.cancelQueries({ queryKey: flowsQueryKeyId });

      const previousFlows: Flow[] | undefined = queryClient.getQueryData(flowsQueryKeyId);

      if (previousFlows) {
        queryClient.setQueryData(
          flowsQueryKeyId,
          previousFlows.map((flow) => (flow.id === newFlow.id ? { ...flow, ...newFlow } : flow)),
        );
      } else {
        console.error('No data found for flowsQueryKeyId');
      }

      return { previousFlows };
    },
    onError: (err, newFlow, context) => {
      queryClient.setQueryData(flowsQueryKeyId, context?.previousFlows);

      showToast({
        title: copy.update.error,
        type: ToastType.Error,
      });
    },
  });

  const deleteMutation = api.flows.deleteOne.useMutation({
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: flowsQueryKeyId });

      const previousFlows: Flow[] | undefined = queryClient.getQueryData(flowsQueryKeyId);

      if (previousFlows) {
        queryClient.setQueryData(
          flowsQueryKeyId,
          previousFlows.filter((flow) => flow.id !== id),
        );
      } else {
        console.error('No data found for flowsQueryKeyId');
      }

      return { previousFlows };
    },
    onError: (err, newFlow, context) => {
      queryClient.setQueryData(flowsQueryKeyId, context?.previousFlows);

      showToast({
        title: copy.delete.error,
        type: ToastType.Error,
      });
    },
    onSuccess: () => {
      utils.flows.getAll.invalidate();
    },
  });

  const { duplicateFlow, isDuplicatingFlow } = useDuplicateFlow();

  const onActivate = (id: string, isActivated: boolean) =>
    updateMutation.mutate({
      id,
      isActivated,
    });

  const onDelete = (id: string) => {
    showDialog({
      title: copy.delete.confirm.title,
      confirmMessage: copy.delete.confirm.description,
      confirmButtonVariant: ButtonVariant.Danger,
      onConfirm: () => {
        deleteMutation.mutate({ id });
      },
    });
  };

  const onDuplicate = (id: string) => {
    duplicateFlow(id);
  };

  const createMutation = api.flows.createOne.useMutation({
    onSuccess: ({ id }) => router.push(urls.single(id)),
    onError: () =>
      showToast({
        title: copy.create.error,
        type: ToastType.Error,
      }),
  });

  const onCreateFlow = () => createMutation.mutate({});

  return (
    <Layout.PageContent>
      <Head>
        <title>Flows</title>
      </Head>

      <div className='flex gap-4 justify-between py-3'>
        <PageTitle>Flows</PageTitle>
        <Button
          variant={ButtonVariant.Secondary}
          icon={<PlusIcon className='h-4 w-4' />}
          text={copy.overview.createFlow}
          onClick={onCreateFlow}
          isLoading={createMutation.isLoading}
          disabled={createMutation.isLoading}
        />
      </div>

      <div className='my-10'>
        <FlowTemplates
          isCloning={isCloningTemplate}
          onCloneTemplate={cloneTemplate}
          showInternalTemplates={showInternalTemplates}
        />
      </div>

      {isLoading && <Collection.Loading className='h-80' />}
      {!isLoading && flows.length > 0 && (
        <Collection className='pt-16 pb-8' ref={aimationParent}>
          {flows.map((flow) => (
            <Link key={flow.id} href={urls.single(flow.id)}>
              <FlowListItem flow={flow} onActivate={onActivate} onDelete={onDelete} onDuplicate={onDuplicate} />
            </Link>
          ))}
        </Collection>
      )}
      {!isLoading && flows.length === 0 && <Collection.Empty className='h-80' />}

      <Dialog isOpen={isCloningTemplate || isDuplicatingFlow} onClose={() => {}}>
        <div className='flex flex-col gap-12 mb-6' data-testid='clone-flow-dialog'>
          <h3 className='text-NeutralDarkDarkest text-base not-italic font-black'>
            {isCloningTemplate ? copy.templates.clone.loading : isDuplicatingFlow && copy.duplicate.loading}
          </h3>
          <div className='flex w-full items-center justify-center'>
            <CircleLoader color='#5557F6' />
          </div>
        </div>
      </Dialog>
    </Layout.PageContent>
  );
};

export default FlowOverviewPage;
FlowOverviewPage.Layout = LayoutTypes.SidebarLayout;

export const getServerSideProps = withSSRSession(async (context: GetServerSidePropsContext) => {
  const { integration, templateName } = context.query;

  const props: {
    template?: { integration: IntegrationAuthApp; templateName: string };
  } = {};

  if (integration && templateName) {
    const template = Object.values(templates[integration as IntegrationAuthApp]).find(
      (template) => template.name === templateName,
    );

    if (template) {
      props.template = {
        integration: integration as IntegrationAuthApp,
        templateName: templateName as string,
      };
    }
  }

  return {
    props,
  };
});
